import MaterialTable, { MTableCell } from '@material-table/core'
import { Chip, Container, Link, Typography } from '@material-ui/core'
import { makeStyles, MuiThemeProvider, useTheme } from '@material-ui/core/styles'
import { cloneDeep, isEqual } from 'lodash'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom'

import { reloadFacilities } from '../../../actions/facilities/facilities.actions'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { PlaygroupsToolbar } from '../../../components/Tables/Toolbars/Playgroups/PlaygroupsToolbar'
import { getChipStyles } from '../../../services/helpers'
import { table } from '../../../services/theme'
import { PLAYGROUP_STATUSES, sortAlphaNum } from '../../../utils'

const cutoffs = [
	{ name: 'The Day Of', number: 0 },
	{ name: 'One Day Before', number: 1 },
	{ name: 'Two Days Before', number: 2 }
]
const useStyles = makeStyles((theme) => ({
	root: {
		// flexGrow: 1,
		paddingTop: '24px',
		paddingLeft: '25px',
		paddingRight: '20px',
		paddingBottom: '75px'
	},
	appBar: {
		top: 'auto',
		bottom: 0,
		paddingRight: '0px !important'
	},
	grid: {},
	gridDouble: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'flex-start'
	},
	fieldWrapper: {
		// '& .MuiTextField-root': { width: '100%', minWidth: '100%' }
	},
	header: {
		fontSize: 12,
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8
	},
	header2: {
		fontSize: 12,
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8
	},
	purple: {
		backgroundColor: '#F0EDFB',
		width: '400px',
		padding: 12,
		paddingTop: 16,
		paddingBottom: 16,
		borderRadius: 10
	},
	purpleText: {
		fontSize: 14,
		lineHeight: '21px',
		color: '#270050',
		padding: 0,
		margin: 0
	},
	toolbarWrapper: {
		display: 'flex',
		flexDirection: 'column',
		padding: '24px 24px 24px 0px',
		color: '#1A1A35'
	},
	chipRow: {
		height: '100%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center'
	}
}))

const FacilityBasicInfo = () => {
	const classes = useStyles()
	const params = useParams()
	const theme = useTheme()
	const dispatch = useDispatch()
	const history = useHistory()
	const location = useLocation()

	const tableRef = useRef(null)
	const toolbarRef = useRef(null)

	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const selectedFacility = useSelector((state) => state.specific.facilities.selected)
	const selectedFilters = useSelector((state) => state.specific.facilities.selectedPlaygroupsFilters)
	const loading = useSelector((state) => state.specific.facilities.loading)

	const [current, setCurrent] = useState(null)
	const [isSaveShown, setIsSaveShown] = useState(false)
	const [data, setData] = useState([])

	const [date, setDate] = useState(moment().format('YYYY-MM-DD'))
	const [playgroupToEdit, setPlaygroupToEdit] = useState(null)

	useEffect(() => {
		dispatch(reloadFacilities(false))
	}, [])

	useEffect(() => {
		if (params.facilityId && selectedFacility) {
			setCurrent(cloneDeep(selectedFacility))
		}
	}, [params.facilityId, selectedFacility, facilities])

	useEffect(() => {
		if (current && selectedFacility) {
			let facilityEqual = !isEqual(current, selectedFacility)

			setIsSaveShown(facilityEqual)
		}
	}, [current, selectedFacility])

	useEffect(() => {
		let filteredData = [...(current?.playgroups || [])]

		if (Object.keys(selectedFilters).some((f) => selectedFilters[f])) {
			if (selectedFilters.type.length) {
				filteredData = filteredData.filter((d) => selectedFilters.type.includes(d.playgroup_type_id))
			}
			if (selectedFilters.kennel_block.length) {
				filteredData = filteredData.filter((d) => selectedFilters.kennel_block.includes(d.kennel_block_id))
			}
			if (selectedFilters.indoor_play_area.length) {
				filteredData = filteredData.filter((d) =>
					selectedFilters.indoor_play_area.includes(d.default_indoor_play_area_id)
				)
			}
			if (selectedFilters.outdoor_play_area.length) {
				filteredData = filteredData.filter((d) =>
					selectedFilters.outdoor_play_area.includes(d.default_outdoor_play_area_id)
				)
			}
			if (selectedFilters.status.length) {
				filteredData = filteredData.filter((d) => selectedFilters.status.includes(d.default_status))
			}
		}
		// sort playgroups
		filteredData = filteredData.sort((a, b) => sortAlphaNum(a.name, b.name, false))
		setData(filteredData)
	}, [current, selectedFilters])

	const editRow = (row) => {
		setPlaygroupToEdit(row)
	}

	// RENDER COLUMNS CUSTOM
	const renderPlaygroupName = (data) => {
		const val = data.name || ''

		return (
			<div>
				<Typography
					style={{
						textTransform: 'capitalize',
						color: '#1A1A35',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: '22px'
					}}>
					{val}
				</Typography>
				<Link
					component={RouterLink}
					style={{
						color: theme.palette.secondary.main,
						marginBottom: 5,
						fontSize: '14px',
						lineHeight: '19px'
					}}
					onClick={(e) => {
						e.stopPropagation()
						editRow(data)
					}}>
					Edit Playgroup
				</Link>
			</div>
		)
	}

	const renderPlaygroupType = (data) => {
		const found = current.playgroup_types?.find((p) => p._id === data.playgroup_type_id)
		return found?.name
	}

	const renderKennelBlock = (data) => {
		const found = current.kennel_blocks?.find((p) => p._id === data.kennel_block_id)
		const val = found?.name || ''

		return (
			val && (
				<div>
					<Chip
						label={val}
						style={{
							...(val ? getChipStyles('purple', val.length > 15) : {})
						}}
					/>
				</div>
			)
		)
	}

	const renderIndoorPlay = (data) => {
		const found = current.areas?.find((p) => p._id === data.default_indoor_play_area_id)
		const val = found?.name || ''

		return (
			<div className={classes.chipRow}>
				{val && (
					<Chip
						label={val}
						style={{
							...(val ? getChipStyles('purple', val.length > 15) : {})
						}}
					/>
				)}
			</div>
		)
	}

	const renderOutdoorPlay = (data) => {
		const found = current.areas?.find((p) => p._id === data.default_outdoor_play_area_id)
		const val = found?.name || ''

		return (
			<div className={classes.chipRow}>
				{val && (
					<Chip
						label={val}
						style={{
							...(val ? getChipStyles('purple', val.length > 15) : {})
						}}
					/>
				)}
			</div>
		)
	}

	const renderStatus = (data) => {
		const val =
			data?.default_status in PLAYGROUP_STATUSES ? PLAYGROUP_STATUSES[data.default_status] : 'disabled'

		return (
			<div className={classes.chipRow}>
				<Chip label={val} style={val ? getChipStyles(val) : null} />
			</div>
		)
	}

	return (
		<div>
			{current ? (
				<Container className={classes.root} maxWidth="auto">
					<MuiThemeProvider theme={table}>
						<PlaygroupsToolbar
							ref={toolbarRef}
							tableRef={tableRef}
							title={'Available Playgroups'}
							darkTitle
							date={date}
							setDate={setDate}
							changeDate={() => {}}
							allowDateChange={false}
							allowFilter={true}
							allowAdd={true}
							playgroupToEdit={playgroupToEdit}
						/>
						<MaterialTable
							tableRef={tableRef}
							date={date}
							style={{ paddingLeft: 24, paddingRight: 24, marginTop: 30 }}
							options={{
								// toolbar: false,
								search: false,
								headerStyle: {
									// header columns style
									color: '#1A1A35',
									textTransform: 'uppercase',
									fontWeight: 800,
									fontSize: '12px',
									lineHeight: '16px',
									padding: '0px 24px 0px 0px'
									// borderTop: '1px solid #D0CFD2',
									// backgroundColor: '#F9F9F9'
								},
								rowStyle: {
									verticalAlign: 'top',
									fontSize: 16
								},
								tableLayout: 'fixed',
								pageSize: 20,
								pageSizeOptions: [5, 10, 20, 50],
								toolbarButtonAlignment: 'left',
								searchFieldAlignment: 'left'
							}}
							columns={[
								{
									title: 'Name',
									// field: 'name',
									width: 210,
									cellStyle: {
										verticalAlign: 'top',
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderPlaygroupName,
									customFilterAndSearch: (term, rowData) => {
										return rowData?.name?.toUpperCase().includes(term.toUpperCase())
									},
									customSort: (a, b) => {
										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b, false)
									}
								},
								{
									title: 'Type',
									// field: 'playgroup_type_id',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderPlaygroupType,
									customSort: (a, b) => {
										a =
											a &&
											current.playgroup_types?.find((p) => p._id === a.playgroup_type_id)
										b =
											b &&
											current.playgroup_types?.find((p) => p._id === b.playgroup_type_id)

										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b, false)
									}
								},
								{
									title: 'Capacity',
									field: 'capacity',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%'
								},
								{
									title: 'Kennel block',
									// field: 'kennels',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderKennelBlock,
									customSort: (a, b) => {
										a = a && current.kennel_blocks?.find((p) => p._id === a.kennel_block_id)
										b = b && current.kennel_blocks?.find((p) => p._id === b.kennel_block_id)

										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b, false)
									}
								},
								{
									title: 'Indoor area',
									// field: 'indoor_play',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderIndoorPlay,
									customSort: (a, b) => {
										a =
											a &&
											current.areas?.find((p) => p._id === a.default_indoor_play_area_id)
										b =
											b &&
											current.areas?.find((p) => p._id === b.default_indoor_play_area_id)

										a = (a && a.name) || ''
										b = (b && b.name) || ''
										return sortAlphaNum(a, b, false)
									}
								},
								{
									title: 'Outdoor area',
									// field: 'outdoor_play',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderOutdoorPlay,
									customSort: (a, b) => {
										a =
											a &&
											current.areas?.find((p) => p._id === a.default_outdoor_play_area_id)
										b =
											b &&
											current.areas?.find((p) => p._id === b.default_outdoor_play_area_id)

										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b, false)
									}
								},
								{
									title: 'Default Status',
									// field: 'status',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '10%',
									render: renderStatus,
									customSort: (a, b) => {
										a =
											a && a?.default_status in PLAYGROUP_STATUSES
												? PLAYGROUP_STATUSES[a.default_status]
												: 'disabled'
										b =
											b && b?.default_status in PLAYGROUP_STATUSES
												? PLAYGROUP_STATUSES[b.default_status]
												: 'disabled'

										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b, false)
									}
								}
							]}
							data={data}
							components={{
								Cell: (props) => (
									<MTableCell
										style={{
											borderBottom: 'none',
											padding: '16px 36px 16px 24px'
										}}
										{...props}
									/>
								),
								Toolbar: (props) => (
									<div className={classes.toolbarWrapper}>
										<Typography
											style={{
												textTransform: 'uppercase',
												fontSize: 16,
												fontWeight: '800',
												lineHeight: '22px'
											}}>
											Available playgroups
										</Typography>
									</div>
								)
							}}
						/>
					</MuiThemeProvider>
				</Container>
			) : null}
			{loading ? <LoadingOverlay /> : null}
		</div>
	)
}

export default FacilityBasicInfo
